import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import BranchSelector from './BranchSelector';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';

function ClassMapping() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId, handleBranchChange } = useBranchFilter();
  const [mappings, setMappings] = useState([]);
  const [loadingMappings, setLoadingMappings] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  
  // Options
  const [academicYears, setAcademicYears] = useState([]);
  const [classes, setClasses] = useState([]);
  const [feeCategories, setFeeCategories] = useState([]);
  
  const [formData, setFormData] = useState({
    academic_year_id: '',
    branch_id: '',
    class_id: '',
    fee_category: '',
    fee_name: '',
    applies_to: 'All',
    amount: '',
    payment_due_date: '',
    status: 'draft' // 'draft' or 'published'
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showDeleteConfirmPopup, setShowDeleteConfirmPopup] = useState(false);
  const [mappingToDelete, setMappingToDelete] = useState(null);
  const [showDeleteSuccessPopup, setShowDeleteSuccessPopup] = useState(false);

  const appliesToOptions = ['All', 'New', 'Returning'];

  const getCurrencySymbol = (currency) => {
    const currencySymbols = {
      PKR: '₨',
      USD: '$',
      EUR: '€',
      GBP: '£',
      AED: 'د.إ',
      SAR: '﷼',
      QAR: '﷼',
      KWD: 'د.ك',
      BHD: '.د.ب',
      OMR: '﷼',
      JOD: 'د.ا',
      LBP: 'ل.ل',
      EGP: '£',
      ILS: '₪',
      TRY: '₺',
      IRR: '﷼',
      IQD: 'ع.د',
      AFN: '؋',
      INR: '₹',
      CAD: 'C$',
      AUD: 'A$',
      JPY: '¥',
      CNY: '¥',
      SGD: 'S$',
      MYR: 'RM',
      THB: '฿',
      IDR: 'Rp',
      PHP: '₱',
      VND: '₫',
      BDT: '৳',
      LKR: '₨',
      NPR: '₨',
      KES: 'KSh',
      NGN: '₦',
      ZAR: 'R'
    };

    return currencySymbols[currency] || currency || '₨';
  };

  const formatCurrency = (amount) => {
    return `${getCurrencySymbol(schoolData?.currency)}${parseFloat(amount || 0).toFixed(2)}`;
  };

  // Helper function to get base class name without section
  const getClassDisplayName = (className) => {
    if (!className) return 'No Class';
    if (className.includes(' - ')) {
      return className.split(' - ')[0].trim();
    }
    return className.trim();
  };

  useEffect(() => {
    const storedSchoolData = localStorage.getItem('schoolData');
    if (!storedSchoolData) {
      navigate('/schoollogin');
      return;
    }
    const parsedSchoolData = JSON.parse(storedSchoolData);
    setSchoolData(parsedSchoolData);
    setLoading(false);

    const refreshSchoolCurrency = async () => {
      if (!parsedSchoolData?.id) return;

      const { data, error } = await supabase
        .from('schools')
        .select('currency')
        .eq('id', parsedSchoolData.id)
        .single();

      if (error || !data?.currency || data.currency === parsedSchoolData.currency) return;

      const updatedSchoolData = { ...parsedSchoolData, currency: data.currency };
      setSchoolData(updatedSchoolData);
      localStorage.setItem('schoolData', JSON.stringify(updatedSchoolData));
    };

    refreshSchoolCurrency();
  }, [navigate]);

  useEffect(() => {
    if (schoolData?.id) {
      fetchAcademicYears();
      fetchClasses();
      fetchFeeCategories();
      fetchMappings();
    }
  }, [schoolData?.id, selectedBranchId]);

  const fetchAcademicYears = async () => {
    try {
      const { data, error } = await supabase
        .from('academic_years')
        .select('*')
        .eq('school_id', schoolData.id)
        .order('start_date', { ascending: false });

      if (error) {
        console.error('Error fetching academic years:', error);
        return;
      }

      setAcademicYears(data || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchClasses = async () => {
    try {
      let query = supabase
        .from('classes')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .order('class_name', { ascending: true });

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      } else {
        query = query.is('branch_id', null);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching classes:', error);
        return;
      }

      setClasses(data || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchFeeCategories = async () => {
    try {
      let query = supabase
        .from('annual_fees_master')
        .select('fee_category')
        .eq('school_id', schoolData.id)
        .eq('status', 'published');

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      } else {
        query = query.is('branch_id', null);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching fee categories:', error);
        return;
      }

      // Get unique categories
      const uniqueCategories = [...new Set((data || []).map(item => item.fee_category))];
      setFeeCategories(uniqueCategories);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchMappings = async () => {
    try {
      setLoadingMappings(true);
      let query = supabase
        .from('class_fee_mapping')
        .select(`
          *,
          academic_years:academic_year_id (id, year_name, start_date, end_date),
          classes:class_id (id, class_name),
          branches:branch_id (id, branch_name)
        `)
        .eq('school_id', schoolData.id)
        .order('created_at', { ascending: false });

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      } else {
        query = query.is('branch_id', null);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching mappings:', error);
        return;
      }

      setMappings(data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoadingMappings(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.academic_year_id) {
      newErrors.academic_year_id = 'Academic Year is required';
    }

    if (!formData.class_id) {
      newErrors.class_id = 'Class is required';
    }

    if (!formData.fee_category) {
      newErrors.fee_category = 'Fee Category is required';
    }

    if (!formData.fee_name.trim()) {
      newErrors.fee_name = 'Fee Name is required';
    }

    if (!formData.applies_to) {
      newErrors.applies_to = 'Applies To is required';
    }

    if (!formData.amount || isNaN(parseFloat(formData.amount)) || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Valid amount is required';
    }

    if (!formData.payment_due_date) {
      newErrors.payment_due_date = 'Payment due date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleMappingSaveError = (error, fallbackMessage) => {
    if (error?.code === '23505' || error?.message?.includes('unique_class_fee_mapping')) {
      setErrors(prev => ({
        ...prev,
        fee_name: 'Fee name already exists'
      }));
      alert('Fee name already exists');
      return;
    }

    alert(fallbackMessage);
  };

  const handleAssign = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const mappingData = {
        ...formData,
        fee_name: formData.fee_name.trim(),
        school_id: schoolData.id,
        branch_id: selectedBranchId || formData.branch_id || null,
        amount: parseFloat(formData.amount),
        status: 'draft',
        updated_at: new Date().toISOString()
      };

      if (editingMapping) {
        // Update existing mapping
        const { error } = await supabase
          .from('class_fee_mapping')
          .update(mappingData)
          .eq('id', editingMapping.id);

        if (error) throw error;
      } else {
        // Create new mapping
        const { error } = await supabase
          .from('class_fee_mapping')
          .insert(mappingData);

        if (error) throw error;
      }

      // Reset form
      setFormData({
        academic_year_id: '',
        branch_id: '',
        class_id: '',
        fee_category: '',
        fee_name: '',
        applies_to: 'All',
        amount: '',
        payment_due_date: '',
        status: 'draft'
      });
      setEditingMapping(null);
      setErrors({});
      await fetchMappings();
      setSuccessMessage('Fee assigned to class successfully!');
      setShowSuccessPopup(true);
    } catch (error) {
      console.error('Error assigning fee:', error);
      handleMappingSaveError(error, 'Failed to assign fee. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePublish = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const mappingData = {
        ...formData,
        fee_name: formData.fee_name.trim(),
        school_id: schoolData.id,
        branch_id: selectedBranchId || formData.branch_id || null,
        amount: parseFloat(formData.amount),
        status: 'published',
        published_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      if (editingMapping) {
        // Update existing mapping
        const { error } = await supabase
          .from('class_fee_mapping')
          .update(mappingData)
          .eq('id', editingMapping.id);

        if (error) throw error;
      } else {
        // Create new mapping
        const { error } = await supabase
          .from('class_fee_mapping')
          .insert(mappingData);

        if (error) throw error;
      }

      // Reset form
      setFormData({
        academic_year_id: '',
        branch_id: '',
        class_id: '',
        fee_category: '',
        fee_name: '',
        applies_to: 'All',
        amount: '',
        payment_due_date: '',
        status: 'draft'
      });
      setEditingMapping(null);
      setErrors({});
      await fetchMappings();
      setSuccessMessage('Fees published successfully! 🎉');
      setShowSuccessPopup(true);
    } catch (error) {
      console.error('Error publishing fees:', error);
      handleMappingSaveError(error, 'Failed to publish fees. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (mapping) => {
    setEditingMapping(mapping);
    setFormData({
      academic_year_id: mapping.academic_year_id || '',
      branch_id: mapping.branch_id || '',
      class_id: mapping.class_id || '',
      fee_category: mapping.fee_category || '',
      fee_name: mapping.fee_name || '',
      applies_to: mapping.applies_to || 'All',
      amount: mapping.amount || '',
      payment_due_date: mapping.payment_due_date || '',
      status: mapping.status || 'draft'
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteClick = (mapping) => {
    setMappingToDelete(mapping);
    setShowDeleteConfirmPopup(true);
  };

  const handleDeleteConfirm = async () => {
    if (!mappingToDelete) return;

    try {
      const { error } = await supabase
        .from('class_fee_mapping')
        .delete()
        .eq('id', mappingToDelete.id);

      if (error) throw error;

      await fetchMappings();
      setShowDeleteConfirmPopup(false);
      setMappingToDelete(null);
      setShowDeleteSuccessPopup(true);
    } catch (error) {
      console.error('Error deleting mapping:', error);
      setShowDeleteConfirmPopup(false);
      setMappingToDelete(null);
      alert('Failed to delete fee mapping. Please try again.');
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmPopup(false);
    setMappingToDelete(null);
  };

  const handleCancel = () => {
    setFormData({
      academic_year_id: '',
      branch_id: '',
      class_id: '',
      fee_category: '',
      fee_name: '',
      applies_to: 'All',
      amount: '',
      payment_due_date: '',
      status: 'draft'
    });
    setEditingMapping(null);
    setErrors({});
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
      />
      
      <SchoolSidebar schoolData={schoolData} />
      
      <div className="main-content">
        <div className="content-header">
          <h1>Class Fee Mapping</h1>
          <p>Assign fees to classes for different academic years</p>
        </div>

        <div className="branch-filter-row" style={{ marginBottom: '1.5rem' }}>
          <BranchSelector
            schoolId={schoolData?.id}
            selectedBranchId={selectedBranchId}
            onBranchChange={handleBranchChange}
          />
        </div>

        <div className="dashboard-section">
          <h2>{editingMapping ? 'Edit Fee Mapping' : 'Assign Fee to Class'}</h2>
          
          <div className="form-section" style={{ width: '100%', maxWidth: '100%' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div className="form-group">
                <label htmlFor="academic_year_id">
                  Academic Year <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <select
                  id="academic_year_id"
                  name="academic_year_id"
                  value={formData.academic_year_id}
                  onChange={handleInputChange}
                  className={errors.academic_year_id ? 'error' : ''}
                >
                  <option value="">Select Academic Year</option>
                  {academicYears.map(year => (
                    <option key={year.id} value={year.id}>
                      {year.year_name || `${new Date(year.start_date).getFullYear()}-${new Date(year.end_date).getFullYear()}`}
                    </option>
                  ))}
                </select>
                {errors.academic_year_id && (
                  <span className="error-text">{errors.academic_year_id}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="class_id">
                  Class <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <select
                  id="class_id"
                  name="class_id"
                  value={formData.class_id}
                  onChange={handleInputChange}
                  className={errors.class_id ? 'error' : ''}
                >
                  <option value="">Select Class</option>
                  {classes.map(cls => (
                    <option key={cls.id} value={cls.id}>
                      {getClassDisplayName(cls.class_name)}
                    </option>
                  ))}
                </select>
                {errors.class_id && (
                  <span className="error-text">{errors.class_id}</span>
                )}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div className="form-group">
                <label htmlFor="fee_category">
                  Fee Category <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <select
                  id="fee_category"
                  name="fee_category"
                  value={formData.fee_category}
                  onChange={handleInputChange}
                  className={errors.fee_category ? 'error' : ''}
                >
                  <option value="">Select Fee Category</option>
                  {feeCategories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                {errors.fee_category && (
                  <span className="error-text">{errors.fee_category}</span>
                )}
                <small style={{ color: '#6B7280', fontSize: '0.875rem' }}>
                  Links to Fees Master
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="fee_name">
                  Fee Name <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <input
                  type="text"
                  id="fee_name"
                  name="fee_name"
                  value={formData.fee_name}
                  onChange={handleInputChange}
                  placeholder="e.g., Tuition Fee, School Trip, Lab Fee"
                  className={errors.fee_name ? 'error' : ''}
                />
                {errors.fee_name && (
                  <span className="error-text">{errors.fee_name}</span>
                )}
                <small style={{ color: '#6B7280', fontSize: '0.875rem' }}>
                  Type a fee name for the selected category
                </small>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div className="form-group">
                <label htmlFor="applies_to">
                  Applies To <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <select
                  id="applies_to"
                  name="applies_to"
                  value={formData.applies_to}
                  onChange={handleInputChange}
                  className={errors.applies_to ? 'error' : ''}
                >
                  {appliesToOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                {errors.applies_to && (
                  <span className="error-text">{errors.applies_to}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="amount">
                  Amount <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className={errors.amount ? 'error' : ''}
                />
                {errors.amount && (
                  <span className="error-text">{errors.amount}</span>
                )}
                <small style={{ color: '#6B7280', fontSize: '0.875rem' }}>
                  System currency: {schoolData?.currency || 'PKR'} ({getCurrencySymbol(schoolData?.currency)})
                </small>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="payment_due_date">
                Payment Due Date <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <input
                type="date"
                id="payment_due_date"
                name="payment_due_date"
                value={formData.payment_due_date}
                onChange={handleInputChange}
                className={errors.payment_due_date ? 'error' : ''}
              />
              {errors.payment_due_date && (
                <span className="error-text">{errors.payment_due_date}</span>
              )}
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button
                type="button"
                onClick={handleAssign}
                disabled={isSubmitting}
                className="btn-secondary"
                style={{ flex: 1 }}
              >
                {isSubmitting ? 'Assigning...' : 'Assign'}
              </button>
              <button
                type="button"
                onClick={handlePublish}
                disabled={isSubmitting}
                className="btn-primary"
                style={{ flex: 1 }}
              >
                {isSubmitting ? 'Publishing...' : 'Publish Fees'}
              </button>
              {editingMapping && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="dashboard-section">
          <h2>Fee Mappings List</h2>
          
          {loadingMappings ? (
            <p>Loading mappings...</p>
          ) : mappings.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '2rem', color: '#6B7280' }}>
              No fee mappings defined yet. Assign your first fee to a class above.
            </p>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Academic Year</th>
                    <th>Branch</th>
                    <th>Class</th>
                    <th>Fee Category</th>
                    <th>Fee Name</th>
                    <th>Applies To</th>
                    <th>Amount</th>
                    <th>Due Date</th>
                    <th>Status</th>
                    <th>Published At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {mappings.map((mapping) => (
                    <tr key={mapping.id}>
                      <td>{mapping.academic_years?.year_name || `AY ${mapping.academic_year_id}`}</td>
                      <td>{mapping.branches?.branch_name || 'Main Branch'}</td>
                      <td>{mapping.classes?.class_name ? getClassDisplayName(mapping.classes.class_name) : 'N/A'}</td>
                      <td>{mapping.fee_category}</td>
                      <td>{mapping.fee_name}</td>
                      <td>{mapping.applies_to}</td>
                      <td>{formatCurrency(mapping.amount)}</td>
                      <td>{mapping.payment_due_date ? new Date(mapping.payment_due_date).toLocaleDateString() : 'N/A'}</td>
                      <td>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: mapping.status === 'published' ? '#10B981' : '#F59E0B',
                          color: 'white',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          textTransform: 'capitalize'
                        }}>
                          {mapping.status}
                        </span>
                      </td>
                      <td>
                        {mapping.published_at 
                          ? new Date(mapping.published_at).toLocaleString() 
                          : 'N/A'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => handleEdit(mapping)}
                            style={{ 
                              padding: '0.5rem 1rem', 
                              fontSize: '0.875rem',
                              backgroundColor: '#10B981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: '500',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#10B981'}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteClick(mapping)}
                            style={{ 
                              padding: '0.5rem 1rem', 
                              fontSize: '0.875rem',
                              backgroundColor: '#EF4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: '500',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#DC2626'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#EF4444'}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Success Popup */}
      {showSuccessPopup && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            backdropFilter: 'blur(6px)',
            animation: 'fadeIn 0.3s ease-out'
          }}
          onClick={() => setShowSuccessPopup(false)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '20px',
              padding: '3rem 2.5rem',
              maxWidth: '550px',
              width: '90%',
              boxShadow: '0 25px 80px rgba(0, 0, 0, 0.4)',
              textAlign: 'center',
              animation: 'slideInUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
              border: '1px solid rgba(16, 185, 129, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 2rem',
              animation: 'scaleInBounce 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
              boxShadow: '0 10px 30px rgba(16, 185, 129, 0.3)'
            }}>
              <svg 
                width="50" 
                height="50" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="white" 
                strokeWidth="3" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                style={{ animation: 'checkmarkDraw 0.6s ease-out 0.3s both' }}
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <h2 style={{
              margin: '0 0 1rem 0',
              color: '#1F2937',
              fontSize: '2rem',
              fontWeight: '800',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Success!
            </h2>
            <p style={{
              margin: '0 0 2.5rem 0',
              color: '#4B5563',
              fontSize: '1.125rem',
              lineHeight: '1.6',
              fontWeight: '500'
            }}>
              {successMessage}
            </p>
            <button
              onClick={() => setShowSuccessPopup(false)}
              style={{
                padding: '1rem 3rem',
                background: 'linear-gradient(135deg, #FF8C42 0%, #E67E22 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '1.125rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 6px 20px rgba(255, 139, 61, 0.4)',
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px) scale(1.02)';
                e.target.style.boxShadow = '0 10px 30px rgba(255, 139, 61, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0) scale(1)';
                e.target.style.boxShadow = '0 6px 20px rgba(255, 139, 61, 0.4)';
              }}
            >
              Got It
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Popup */}
      {showDeleteConfirmPopup && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
            backdropFilter: 'blur(6px)',
            animation: 'fadeIn 0.3s ease-out'
          }}
          onClick={handleDeleteCancel}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '20px',
              padding: '3rem 2.5rem',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 25px 80px rgba(0, 0, 0, 0.4)',
              textAlign: 'center',
              animation: 'slideInUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
              border: '1px solid rgba(239, 68, 68, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 2rem',
              animation: 'scaleInBounce 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
              boxShadow: '0 10px 30px rgba(239, 68, 68, 0.3)'
            }}>
              <svg 
                width="50" 
                height="50" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="white" 
                strokeWidth="3" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>
            <h2 style={{
              margin: '0 0 1rem 0',
              color: '#1F2937',
              fontSize: '1.75rem',
              fontWeight: '800'
            }}>
              Confirm Deletion
            </h2>
            <p style={{
              margin: '0 0 2.5rem 0',
              color: '#4B5563',
              fontSize: '1.125rem',
              lineHeight: '1.6',
              fontWeight: '500'
            }}>
              Are you sure you want to delete this fee mapping?<br />
              <span style={{ color: '#EF4444', fontWeight: '600' }}>
                This action cannot be undone.
              </span>
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button
                onClick={handleDeleteCancel}
                style={{
                  padding: '1rem 2.5rem',
                  backgroundColor: '#E5E7EB',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#D1D5DB';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#E5E7EB';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                style={{
                  padding: '1rem 2.5rem',
                  background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 6px 20px rgba(239, 68, 68, 0.4)',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px) scale(1.02)';
                  e.target.style.boxShadow = '0 10px 30px rgba(239, 68, 68, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0) scale(1)';
                  e.target.style.boxShadow = '0 6px 20px rgba(239, 68, 68, 0.4)';
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Success Popup */}
      {showDeleteSuccessPopup && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
            backdropFilter: 'blur(6px)',
            animation: 'fadeIn 0.3s ease-out'
          }}
          onClick={() => setShowDeleteSuccessPopup(false)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '20px',
              padding: '3rem 2.5rem',
              maxWidth: '550px',
              width: '90%',
              boxShadow: '0 25px 80px rgba(0, 0, 0, 0.4)',
              textAlign: 'center',
              animation: 'slideInUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
              border: '1px solid rgba(16, 185, 129, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 2rem',
              animation: 'scaleInBounce 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
              boxShadow: '0 10px 30px rgba(16, 185, 129, 0.3)'
            }}>
              <svg 
                width="50" 
                height="50" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="white" 
                strokeWidth="3" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                style={{ animation: 'checkmarkDraw 0.6s ease-out 0.3s both' }}
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <h2 style={{
              margin: '0 0 1rem 0',
              color: '#1F2937',
              fontSize: '2rem',
              fontWeight: '800',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Deleted Successfully!
            </h2>
            <p style={{
              margin: '0 0 2.5rem 0',
              color: '#4B5563',
              fontSize: '1.125rem',
              lineHeight: '1.6',
              fontWeight: '500'
            }}>
              Fee mapping has been deleted successfully! 🎉
            </p>
            <button
              onClick={() => setShowDeleteSuccessPopup(false)}
              style={{
                padding: '1rem 3rem',
                background: 'linear-gradient(135deg, #FF8C42 0%, #E67E22 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '1.125rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 6px 20px rgba(255, 139, 61, 0.4)',
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px) scale(1.02)';
                e.target.style.boxShadow = '0 10px 30px rgba(255, 139, 61, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0) scale(1)';
                e.target.style.boxShadow = '0 6px 20px rgba(255, 139, 61, 0.4)';
              }}
            >
              Got It
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes scaleInBounce {
          0% {
            opacity: 0;
            transform: scale(0);
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes checkmarkDraw {
          0% {
            stroke-dasharray: 0 50;
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            stroke-dasharray: 50 0;
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

export default ClassMapping;
